import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '@/styles/tailwind-utils';

const MOCK_ADDRESSES = [
  'Str. Victoriei 12, Sector 1, București',
  'Bd. Unirii 45, Sector 3, București',
  'Calea Floreasca 100, Sector 1, București',
  'Str. Mihai Eminescu 8, Cluj-Napoca',
  'Str. Republicii 23, Brașov',
  'Bd. Decebal 14, Iași',
  'Str. Independenței 5, Timișoara',
  'Calea Dorobanților 32, Sector 1, București',
  'Str. Lipscani 18, Sector 3, București',
  'Bd. Mamaia 200, Constanța',
];

interface AddressTypeaheadProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

export function AddressTypeahead({
  label,
  placeholder,
  value,
  onChange,
  error,
}: AddressTypeaheadProps) {
  const inputId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    if (value.length < 3) return [];
    const q = value.toLowerCase();
    return MOCK_ADDRESSES.filter((a) => a.toLowerCase().includes(q));
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
    setIsOpen(true);
  }

  function handleSelect(address: string) {
    onChange(address);
    setIsOpen(false);
  }

  const showDropdown = isOpen && suggestions.length > 0;

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-foreground">
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        autoComplete="off"
        className={cn(
          'h-10 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm',
          'placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1',
          error && 'border-destructive focus:ring-destructive',
        )}
      />
      {showDropdown && (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-sm border border-border bg-surface shadow-md"
        >
          {suggestions.map((addr) => (
            <li
              key={addr}
              role="option"
              aria-selected={value === addr}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(addr);
              }}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-muted"
            >
              <span className="mr-2 text-muted-foreground" aria-hidden="true">
                📍
              </span>
              {addr}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
